import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { saveUser } from "@/services/userServices/userActions";
import type { SelectOption } from "@/utils/selectOptions";
import PersonalInformationView from "./PersonalInformationView";
import type { PickedImage } from "./ImageUploadContainer";

type FieldState = "valid" | "invalid" | null;

/** The new-user form as it is edited: text fields plus the `enabled` checkbox. */
type UserDraft = Record<string, string | boolean | undefined>;

const selectRoleData = [
  { value: "CONTENTEDITOR", label: "Content Editor" },
  { value: "SALESMANAGER", label: "Sales Manager" },
  { value: "CONTENTDIRECTOR", label: "Content Director" },
  { value: "CONTENTCOORDINATOR", label: "Content Coordinator" },
  { value: "ADMINISTRATOR", label: "Administrator" },
];

const initialValidation = {
  name: { State: null as FieldState, error: "" },
  email: { State: null as FieldState, error: "" },
  emailverif: { State: null as FieldState, error: "" },
  role: { State: null as FieldState, error: "" },
  FormValid: false,
};

export default function PersonalInformationContainer() {
  const dispatch = useAppDispatch();
  const userFromStore = useAppSelector((s) => s.users.register_user);

  const [user, setUser] = useState(userFromStore || {});
  const [image, setImage] = useState<PickedImage>({});
  const [formValidation, setFormValidation] = useState(initialValidation);

  useEffect(() => {
    if (userFromStore) {
      setUser(userFromStore);
    }
  }, [userFromStore]);

  const validate = useCallback((inputName: string, nextUser: UserDraft) => {
    const v = { ...formValidation, FormValid: false };
    // The four fields validated below are all text inputs.
    const val = nextUser[inputName] as string | undefined;

    if (inputName === "name") v.name.State = (!val || val.length < 4) ? "invalid" : "valid";
    if (inputName === "email") {
      const ok = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(val as string);
      v.email.State = (!val || !ok) ? "invalid" : "valid";
    }
    if (inputName === "emailverif") v.emailverif.State = (!val || nextUser.email !== val) ? "invalid" : "valid";
    if (inputName === "role") v.role.State = (!val ? "invalid" : "valid");

    if (v.name.State === "valid" && v.email.State === "valid" && v.emailverif.State === "valid" && v.role.State === "valid") {
      v.FormValid = true;
    }
    return v;
  }, [formValidation]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    const next = { ...user, [name]: type === "checkbox" ? checked : value };
    setUser(next);

    const nv = validate(name, next);
    setFormValidation(nv);

    if (nv.FormValid) {
      dispatch(saveUser(next));
    }
  }, [user, validate, dispatch]);

  const handleRoleChange = useCallback((opt: SelectOption | null) => {
    const next = { ...user, role: opt ? opt.value : "" };
    setUser(next);

    const nv = validate("role", next);
    setFormValidation(nv);

    if (nv.FormValid) {
      dispatch(saveUser(next));
    }
  }, [user, validate, dispatch]);

  const handleImageUpload = useCallback((imgObj: PickedImage) => {
    setImage(imgObj);
  }, []);

  const roleSelectValue = useMemo(
    () => selectRoleData.find((o) => o.value === user.role) || null,
    [user.role]
  );

  return (
    <PersonalInformationView
      user={user}
      image={image}
      formValidation={formValidation}
      selectRoleData={selectRoleData}
      roleSelectValue={roleSelectValue}
      onInputChange={handleInputChange}
      onRoleChange={handleRoleChange}
      onImageUpload={handleImageUpload}
    />
  );
}
